import { useEffect, useRef, useState } from "react";

const THEME_KEY = "aiSommelierTheme";

export function useTheme() {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem(THEME_KEY) || "modern";
    // 初期化時に即座にテーマを適用
    if (typeof document !== 'undefined') {
      const tokens: Record<string, Record<string, string>> = {
        modern: { "--theme-bg-primary": "#F8FAFC", "--theme-text-primary": "#1E293B", "--theme-accent-primary": "#2563EB", "--theme-accent-secondary": "#3B82F6", "--theme-bg-secondary": "#FFFFFF", "--theme-text-secondary": "#64748B", "--theme-border": "#E2E8F0", "--theme-shadow": "rgba(0, 0, 0, 0.1)" },
        casual: { "--theme-bg-primary": "#ffffff", "--theme-text-primary": "#222", "--theme-accent-primary": "#eea730", "--theme-accent-secondary": "#f4c430", "--theme-bg-secondary": "#f8f9fa", "--theme-text-secondary": "#666", "--theme-border": "#e0e0e0", "--theme-shadow": "rgba(0, 0, 0, 0.1)" },
        natural: { "--theme-bg-primary": "#faf7f0", "--theme-text-primary": "#2b3a2a", "--theme-accent-primary": "#7aa652", "--theme-accent-secondary": "#8bb85a", "--theme-bg-secondary": "#f5f1e8", "--theme-text-secondary": "#5a6b5a", "--theme-border": "#d4c4a8", "--theme-shadow": "rgba(0, 0, 0, 0.1)" },
        gorgeous: { "--theme-bg-primary": "#0f0f10", "--theme-text-primary": "#f5f3ef", "--theme-accent-primary": "#d4af37", "--theme-accent-secondary": "#e6c547", "--theme-bg-secondary": "#1a1a1c", "--theme-text-secondary": "#d4d2ce", "--theme-border": "#3a3a3c", "--theme-shadow": "rgba(0, 0, 0, 0.3)" },
        minimal: { "--theme-bg-primary": "#FFFFFF", "--theme-text-primary": "#2D3748", "--theme-accent-primary": "#4A5568", "--theme-accent-secondary": "#2D3748", "--theme-bg-secondary": "#F8F9FA", "--theme-text-secondary": "#718096", "--theme-border": "#E2E8F0", "--theme-shadow": "rgba(0, 0, 0, 0.1)" },
        resort: { "--theme-bg-primary": "#E6FFFA", "--theme-text-primary": "#234E52", "--theme-accent-primary": "#38B2AC", "--theme-accent-secondary": "#319795", "--theme-bg-secondary": "#B2F5EA", "--theme-text-secondary": "#4A5568", "--theme-border": "#81E6D9", "--theme-shadow": "rgba(0, 0, 0, 0.1)" },
        vintage: { "--theme-bg-primary": "#F7F3E9", "--theme-text-primary": "#8B4513", "--theme-accent-primary": "#D2691E", "--theme-accent-secondary": "#CD853F", "--theme-bg-secondary": "#E8D5B7", "--theme-text-secondary": "#A0522D", "--theme-border": "#D2B48C", "--theme-shadow": "rgba(0, 0, 0, 0.1)" },
        feminine: { "--theme-bg-primary": "#FDF2F8", "--theme-text-primary": "#831843", "--theme-accent-primary": "#EC4899", "--theme-accent-secondary": "#F472B6", "--theme-bg-secondary": "#FCE7F3", "--theme-text-secondary": "#9D174D", "--theme-border": "#F9A8D4", "--theme-shadow": "rgba(0, 0, 0, 0.1)" },
        industrial: { "--theme-bg-primary": "#F1F5F9", "--theme-text-primary": "#1E293B", "--theme-accent-primary": "#64748B", "--theme-accent-secondary": "#475569", "--theme-bg-secondary": "#E2E8F0", "--theme-text-secondary": "#475569", "--theme-border": "#CBD5E1", "--theme-shadow": "rgba(0, 0, 0, 0.1)" },
        nightLuxury: { "--theme-bg-primary": "#0F172A", "--theme-text-primary": "#F8FAFC", "--theme-accent-primary": "#8B5CF6", "--theme-accent-secondary": "#A78BFA", "--theme-bg-secondary": "#1E293B", "--theme-text-secondary": "#CBD5E1", "--theme-border": "#334155", "--theme-shadow": "rgba(0, 0, 0, 0.3)" }
      };
      const vars = tokens[saved] ?? tokens.modern;
      const root = document.documentElement;
      Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
      if (vars["--theme-bg-primary"]) {
        document.body.style.backgroundColor = vars["--theme-bg-primary"];
      }
      if (vars["--theme-text-primary"]) {
        document.body.style.color = vars["--theme-text-primary"];
      }
    }
    return saved;
  });
  const lastApplied = useRef(theme);
  const bcRef = useRef<BroadcastChannel | null>(null);

  // CSS変数を :root に適用
  const applyTheme = (t: string) => {
    if (lastApplied.current === t) return;
    lastApplied.current = t;

    // テーマ -> CSS変数マップ
    const tokens: Record<string, Record<string, string>> = {
      modern: {
        "--theme-bg-primary": "#F8FAFC",
        "--theme-bg-secondary": "#FFFFFF",
        "--theme-text-primary": "#1E293B",
        "--theme-text-secondary": "#64748B",
        "--theme-accent-primary": "#2563EB",
        "--theme-accent-secondary": "#3B82F6",
        "--theme-border": "#E2E8F0",
        "--theme-shadow": "rgba(0, 0, 0, 0.1)"
      },
      casual: {
        "--theme-bg-primary": "#ffffff",
        "--theme-bg-secondary": "#f8f9fa",
        "--theme-text-primary": "#222",
        "--theme-text-secondary": "#666",
        "--theme-accent-primary": "#eea730",
        "--theme-accent-secondary": "#f4c430",
        "--theme-border": "#e0e0e0",
        "--theme-shadow": "rgba(0, 0, 0, 0.1)"
      },
      natural: {
        "--theme-bg-primary": "#faf7f0",
        "--theme-bg-secondary": "#f5f1e8",
        "--theme-text-primary": "#2b3a2a",
        "--theme-text-secondary": "#5a6b5a",
        "--theme-accent-primary": "#7aa652",
        "--theme-accent-secondary": "#8bb85a",
        "--theme-border": "#d4c4a8",
        "--theme-shadow": "rgba(0, 0, 0, 0.1)"
      },
      gorgeous: {
        "--theme-bg-primary": "#0f0f10",
        "--theme-bg-secondary": "#1a1a1c",
        "--theme-text-primary": "#f5f3ef",
        "--theme-text-secondary": "#d4d2ce",
        "--theme-accent-primary": "#d4af37",
        "--theme-accent-secondary": "#e6c547",
        "--theme-border": "#3a3a3c",
        "--theme-shadow": "rgba(0, 0, 0, 0.3)"
      },
      minimal: {
        "--theme-bg-primary": "#FFFFFF",
        "--theme-bg-secondary": "#F8F9FA",
        "--theme-text-primary": "#2D3748",
        "--theme-text-secondary": "#718096",
        "--theme-accent-primary": "#4A5568",
        "--theme-accent-secondary": "#2D3748",
        "--theme-border": "#E2E8F0",
        "--theme-shadow": "rgba(0, 0, 0, 0.1)"
      },
      resort: {
        "--theme-bg-primary": "#E6FFFA",
        "--theme-bg-secondary": "#B2F5EA",
        "--theme-text-primary": "#234E52",
        "--theme-text-secondary": "#4A5568",
        "--theme-accent-primary": "#38B2AC",
        "--theme-accent-secondary": "#319795",
        "--theme-border": "#81E6D9",
        "--theme-shadow": "rgba(0, 0, 0, 0.1)"
      },
      vintage: {
        "--theme-bg-primary": "#F7F3E9",
        "--theme-bg-secondary": "#E8D5B7",
        "--theme-text-primary": "#8B4513",
        "--theme-text-secondary": "#A0522D",
        "--theme-accent-primary": "#D2691E",
        "--theme-accent-secondary": "#CD853F",
        "--theme-border": "#D2B48C",
        "--theme-shadow": "rgba(0, 0, 0, 0.1)"
      },
      feminine: {
        "--theme-bg-primary": "#FDF2F8",
        "--theme-bg-secondary": "#FCE7F3",
        "--theme-text-primary": "#831843",
        "--theme-text-secondary": "#9D174D",
        "--theme-accent-primary": "#EC4899",
        "--theme-accent-secondary": "#F472B6",
        "--theme-border": "#F9A8D4",
        "--theme-shadow": "rgba(0, 0, 0, 0.1)"
      },
      industrial: {
        "--theme-bg-primary": "#F1F5F9",
        "--theme-bg-secondary": "#E2E8F0",
        "--theme-text-primary": "#1E293B",
        "--theme-text-secondary": "#475569",
        "--theme-accent-primary": "#64748B",
        "--theme-accent-secondary": "#475569",
        "--theme-border": "#CBD5E1",
        "--theme-shadow": "rgba(0, 0, 0, 0.1)"
      },
      nightLuxury: {
        "--theme-bg-primary": "#0F172A",
        "--theme-bg-secondary": "#1E293B",
        "--theme-text-primary": "#F8FAFC",
        "--theme-text-secondary": "#CBD5E1",
        "--theme-accent-primary": "#8B5CF6",
        "--theme-accent-secondary": "#A78BFA",
        "--theme-border": "#334155",
        "--theme-shadow": "rgba(0, 0, 0, 0.3)"
      }
    };
    
    const vars = tokens[t] ?? tokens.modern;
    const root = document.documentElement;
    Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));

    // bodyの背景色も更新
    if (vars["--theme-bg-primary"]) {
      document.body.style.backgroundColor = vars["--theme-bg-primary"];
    }
    if (vars["--theme-text-primary"]) {
      document.body.style.color = vars["--theme-text-primary"];
    }

    // デバッグ（DEVのみ）
    if (import.meta.env.DEV) {
      console.log("🎨 CSS variables applied:", t, vars);
    }
  };

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    // BroadcastChannel
    try {
      bcRef.current = new BroadcastChannel("ai-sommelier-theme");
      bcRef.current.onmessage = (e) => {
        if (e?.data?.type === "THEME_SET") {
          setTheme(e.data.value);
          if (import.meta.env.DEV) console.log("Theme changed via BroadcastChannel:", e.data.value);
        }
      };
    } catch {}

    // storage イベント
    const onStorage = (e: StorageEvent) => {
      if (e.key === THEME_KEY && e.newValue) {
        setTheme(e.newValue);
        if (import.meta.env.DEV) console.log("Theme changed via storage:", e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);

    // 保険のポーリング（5秒間隔）
    const interval = setInterval(() => {
      const t = localStorage.getItem(THEME_KEY);
      if (t && t !== lastApplied.current) {
        setTheme(t);
        if (import.meta.env.DEV) console.log("Theme changed via interval:", t);
      }
    }, 5000);

    // タブ復帰時に同期待ち
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        const t = localStorage.getItem(THEME_KEY);
        if (t) setTheme(t);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(interval);
      bcRef.current?.close();
    };
  }, []);

  // 外部からテーマ更新
  const setAndBroadcast = (t: string) => {
    localStorage.setItem(THEME_KEY, t);
    setTheme(t);
    try { 
      bcRef.current?.postMessage({ type: "THEME_SET", value: t }); 
    } catch {}
  };

  return { theme, setTheme: setAndBroadcast };
}


